// inv/src/components/ui/toggle.jsx
import { useId, useCallback } from "react";
import { Check, X } from "lucide-react";

const SIZES = {
  xs: { track: "w-10 h-5", thumb: 16 },
  sm: { track: "w-12 h-6", thumb: 20 },
  md: { track: "w-14 h-7", thumb: 24 },
  lg: { track: "w-16 h-8", thumb: 28 },
};

const COLORS = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  accent: "bg-accent",
  success: "bg-success",
  info: "bg-info",
  warning: "bg-warning",
  error: "bg-error",
};

export default function ToggleNeo({
  checked,
  onChange,
  disabled = false,
  id,
  size = "sm",
  color = "secondary",
  className = "",
}) {
  const autoId = useId();
  const uid = id || autoId;
  const { track, thumb } = SIZES[size] || SIZES.sm;
  const colorClass = COLORS[color] || COLORS.secondary;

  const thumbLeft = checked ? `calc(100% - ${thumb}px - 2px)` : "2px";
  const fillWidth = checked ? "calc(100% - 4px)" : `${thumb + 4}px`;

  const toggle = useCallback(() => {
    if (!disabled) onChange(!checked);
  }, [disabled, checked, onChange]);

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowLeft") onChange(false);
    if (e.key === "ArrowRight") onChange(true);
    if (e.key === "Enter" || e.key === " ") onChange(!checked);
  };

  return (
    <button
      id={uid}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      className={[
        "inline-flex select-none items-center gap-2",
        "focus:outline-none",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      ].join(" ")}
    >
      <div
        className={[
          "relative rounded-full p-[2px] shadow-inner",
          track,
          "bg-base-200/60 backdrop-blur-sm",
        ].join(" ")}
        onClick={toggle}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div
          className={[
            "absolute left-[2px] top-[2px] bottom-[2px] rounded-full",
            colorClass,
            "bg-gradient-to-tr from-white/20 via-transparent to-black/10",
            "transition-all duration-300",
            "motion-reduce:transition-none",
            checked ? "brightness-90 saturate-125" : "",
          ].join(" ")}
          style={{ width: fillWidth }}
          onClick={toggle}
          onMouseDown={(e) => e.preventDefault()}
        />

        <div
          className={[
            "absolute top-1/2 -translate-y-1/2 rounded-full bg-base-100 shadow-md",
            "transition-[left,transform] duration-300",
            "motion-reduce:transition-none",
          ].join(" ")}
          style={{ width: thumb, height: thumb, left: thumbLeft }}
          onClick={toggle}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div
            className={[
              "flex h-full w-full items-center justify-center",
              "transition-transform duration-300",
              checked ? "rotate-0 scale-100" : "rotate-180 scale-[0.95]",
              "motion-reduce:transition-none motion-reduce:rotate-0",
            ].join(" ")}
          >
            {checked ? (
              <Check className="h-3.5 w-3.5 opacity-80 pointer-events-none" />
            ) : (
              <X className="h-3.5 w-3.5 opacity-60 pointer-events-none" />
            )}
          </div>
        </div>

        <div className="pointer-events-none absolute inset-0 rounded-full [mask-image:linear-gradient(#000,transparent)] opacity-40">
          <div className="absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-white/10" />
        </div>
      </div>
    </button>
  );
}
